import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { EnquiryForm } from "../components/site/EnquiryForm";
import { Eyebrow, FadeUp, EASE } from "../components/site/Reveal";
import { SERVICES } from "../data/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  if (idx < 0) return <Navigate to="/services" replace />;
  const s = SERVICES[idx];
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const words = s.title.split(" ");
  const mid = Math.ceil(words.length / 2);

  return (
    <>
      <PageHero testid="service-detail-hero" eyebrow={`Service ${s.no} / 06`}
        lines={[words.slice(0, mid).join(" "), <span className="italic text-[#E3C88A]">{words.slice(mid).join(" ") || "\u00A0"}</span>]} />
      <section className="paper py-20 sm:py-28" data-testid="service-detail-body">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
            <motion.div variants={{ hidden: { clipPath: "inset(14% 14% 14% 14% round 24px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 24px)" } }} transition={{ duration: 1.4, ease: EASE }}
              className="relative aspect-[4/5] overflow-hidden">
              <img src={s.image} alt={s.title} className="absolute inset-0 h-full w-full object-cover" data-testid="service-detail-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070F1E]/60 to-transparent" />
              <span className="absolute bottom-6 left-6 font-mono text-xs tracking-[0.25em] text-[#E3C88A]">RIDHANYA / {s.no}</span>
            </motion.div>
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <Eyebrow>What we do</Eyebrow>
            <FadeUp><p className="mt-6 font-display text-3xl sm:text-4xl text-[#0A192F] leading-snug" data-testid="service-detail-description">{s.body}</p></FadeUp>
            <ul className="mt-12" data-testid="service-detail-scope">
              {s.scope.map((item, i) => (
                <FadeUp as="li" key={item} delay={i * 0.06} className="flex items-center gap-4 border-t border-[#0A192F]/15 py-5 text-[#0A192F] text-base sm:text-lg">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#0A192F] text-[#E3C88A]"><Check className="h-4 w-4" /></span>{item}
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section className="bg-[#EDE5D6] py-20 sm:py-28" data-testid="service-detail-enquiry">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Request a consultation</Eyebrow>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl text-[#0A192F] leading-tight">Tell us about your matter.</h2>
          </div>
          <div className="lg:col-span-8"><EnquiryForm defaultService={s.title} /></div>
        </div>
      </section>
      <Link to={`/services/${next.slug}`} data-testid="service-next-link" className="group block bg-[#070F1E] grain py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex items-end justify-between gap-6">
          <div>
            <Eyebrow dark>Next service</Eyebrow>
            <p className="mt-6 font-display text-4xl sm:text-7xl text-[#F4EEE3] transition-transform duration-700 group-hover:translate-x-4">{next.title}</p>
          </div>
          <ArrowUpRight className="h-12 w-12 shrink-0 text-[#C5A059] transition-transform duration-700 group-hover:rotate-45" />
        </div>
      </Link>
    </>
  );
}
